import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import DaVinciSketch from "@/components/DaVinciSketch";
import { toast } from "sonner";

export default function LoginPage() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (_err) {
      toast.error(t("login.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-grain text-cream relative flex items-center overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 smoke-layer opacity-30" />
      <DaVinciSketch className="absolute right-[-100px] top-[60px] w-[600px] h-[600px] opacity-30 hidden md:block" />

      <div className="relative max-w-[640px] w-full mx-auto px-6 md:px-12 py-20">
        <Link
          to="/"
          className="font-mono text-[10px] tracking-widest2 uppercase text-cream/55 hover:text-gold transition-colors"
          data-testid="login-back"
        >
          ← {t("login.back")}
        </Link>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
          className="mt-10 font-display text-5xl md:text-6xl leading-tight text-cream"
        >
          {t("login.title")}
        </motion.h1>
        <p className="mt-4 text-cream/60 font-light">{t("login.sub")}</p>

        <form onSubmit={onSubmit} className="mt-14 space-y-8" data-testid="login-form">
          <div>
            <label className="font-mono text-[10px] tracking-widest2 uppercase text-cream/55 block mb-2">
              {t("login.email")}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-line"
              data-testid="login-email"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-widest2 uppercase text-cream/55 block mb-2">
              {t("login.password")}
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-line"
              data-testid="login-password"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="btn-luxe-solid disabled:opacity-50 disabled:cursor-wait"
            data-testid="login-submit"
          >
            {submitting ? t("login.submitting") : t("login.submit")}
            <span className="arrow-tick" />
          </button>
        </form>
      </div>
    </div>
  );
}
